import _ from 'lodash';
import * as api from '../../../api';
import * as store from '../../../reducers/store';

import { getSourceFieldValue } from '../index';

type FieldScoreConfiguration = Pick<
  store.FormFieldWithConfig,
  'options' | 'value' | 'primitive' | 'optionsMetadata'
>;
export const isInScoreRange = ({
  fields,
  page,
  rank,
  min,
  max,
}: {
  fields: store.FormFieldWithConfig[];
  page: number;
  rank: number;
  min: number | null;
  max: number | null;
}): boolean => {
  const scores = fieldScoreUpTo({ fields, page, rank });
  const totalScore = scores[api.PrevisitForm.DEFAULT_SCORE_TYPE] || 0;
  if ((min || min === 0) && totalScore < min) return false;
  if ((max || max === 0) && totalScore > max) return false;
  return true;
};

export const fieldScoreUpTo = ({
  fields,
  page,
  rank,
}: {
  fields: store.FormFieldWithConfig[];
  page: number;
  rank: number;
}): Record<string, number> => {
  const pastFields = fieldsUpTo({ fields, targetPage: page, targetRank: rank });
  return fieldsScores(pastFields);
};

export const fieldsScores = (
  fields: FieldScoreConfiguration[],
): Record<string, number> =>
  fields.reduce(
    (scores: Record<string, number>, field: FieldScoreConfiguration) =>
      addScores(scores, fieldScores(field)),
    {},
  );

/**
 * Convert user answer to a form scores
 * @param field the target field to get scores of
 * @return the scores for the field
 */
export const fieldScores = (
  field: FieldScoreConfiguration,
): Record<string, number> => {
  const defaultScores = {};
  if (!field.options?.length || !field.value) return defaultScores;
  const selectedOptions = getSourceFieldValue(field.value, field.primitive);
  return selectedOptions.reduce((prevScores, option) => {
    const optionMetadata = field.optionsMetadata?.[option];
    return addScores(
      prevScores,
      optionMetadata?.scores || {
        [api.PrevisitForm.DEFAULT_SCORE_TYPE]: optionMetadata?.score || 0,
      },
    );
  }, defaultScores);
};

const addScores = (
  targetScores: Record<string, number>,
  sourceScores: Record<string, number>,
) => {
  return _.mergeWith(
    { ...targetScores },
    sourceScores,
    (objValue = 0, srcValue = 0) => {
      return objValue + srcValue;
    },
  );
};

/**
 * Filter to get only fields before given field
 * @param fields all fields from the form
 * @param targetField the target field
 * @return all fields before target fields
 */
const fieldsUpTo = ({
  fields,
  targetPage,
  targetRank,
}: {
  fields: store.FormFieldWithConfig[];
  targetPage: number;
  targetRank: number;
}): store.FormFieldWithConfig[] => {
  return fields.filter(
    ({ page, rank }) =>
      page < targetPage || (page === targetPage && rank < targetRank),
  );
};
